import decode from 'jwt-decode';
import i18n from '../i18n';

const isProxy = window.location.hostname === 'vcap.me';
const TOKEN_KEY = 'Sponda-App';
const HOST = isProxy ? 'https://kohteet.sponda.fi' : window.initialToolConfig.host;

export default class DataService {
	// Initializing important variables
	constructor() {
		this.fetch = this.fetch.bind(this) // React binding stuff
	}

	loggedIn() {
		// Checks if there is a saved token and it's still valid
		const token = this.getToken(); // GEtting token from localstorage
		return !!token && !this.isTokenExpired(token) // handwaiving here
	}

	isTokenExpired(token) {
		try {
			const decoded = decode(token);
			if (decoded.exp < Date.now() / 1000) { // Checking if token is expired. N
				return true;
			}
			else
				return false;
		} catch (err) {
			return false;
		}
	}

	getToken() {
		// Retrieves the user token from localStorage
		return localStorage.getItem(TOKEN_KEY);
	}

	fetch(url, options) {
		// performs api calls sending the required authentication headers
		const headers = {
			Accept: 'application/json',
			'Content-Type': 'application/json',
			'X-Requested-With': 'XHR',
		};

		// Setting Authorization header
		// Authorization: Bearer xxxxxxx.xxxxxxxx.xxxxxx
		if (this.loggedIn()) {
			headers.Authorization = 'Bearer ' + this.getToken();
		}

		// Every fetch should return in 90 seconds, else reload the browser.
		let fetchTimeout = setTimeout(() => {
			console.warn('Reloading browser due to 90 second fetch...');
			window.location.reload();
		}, 1000 * 90);

		return fetch(`${HOST}/${i18n.language}/api/v1` + url, { headers, ...options })
			.then(this._checkStatus)
			.then(response => {
				clearTimeout(fetchTimeout);

				return response.json()
			})
	}

	_checkStatus(response) {
		// raises an error in case response status is not a success
		if (response.status >= 200 && response.status < 300) { // Success status lies between 200 to 300
			return response;
		} else {
			// console.warn(response);
			// var error = new Error(response.statusText);
			// error.response = response;
			// error.message = 'An error occurred.';
			// throw error;
			return response;
		}
	}

	// TOKEN
	getStageToken = () => this.fetch('/token/', { method: 'GET' }).then(response => response.result.token);

	// TEMPLATES
	getTemplates = () => this.fetch('/templates/', { method: 'GET' });

  // CONFIG
  getConfig = () => this.fetch('/config/', { method: 'GET' }).then(response => response.result.config);

  // PICTOGRAMS
  getPictograms = () => this.fetch('/pictograms/', { method: 'GET' }).then(response => response.result.pictograms);

  // USER
  getUser = () => this.fetch('/users/current/', { method: 'GET' }).then(response => response.result.user);
	getUsers = () => this.fetch('/users/', { method: 'GET' }).then(response => response.result.users);
	requestForgottenPasswordEmail = data => this.fetch('/forgot-password/', { method: 'POST', body: JSON.stringify(data) });
	changePassword = data => this.fetch('/change-password/', { method: 'POST', body: JSON.stringify(data) });

	// BUILDING
	getBuildings = () => this.fetch('/buildings/', { method: 'GET' }).then(response => response.result.buildings);
	getBuilding = slug => this.fetch(`/buildings/${slug}/`, { method: 'GET' }).then(response => response.result.building);

  // SPACES
  getSpaces = () => this.fetch('/spaces/', { method: 'GET' }).then(response => {
	  const spaces = response.result.spaces;
	  spaces.forEach(space => {
		  space.isFree = ['free', 'soonfree'].includes(space.status);
	  });
	  return spaces;
  });

  // BROCHURE
  addBrochure = data => this.fetch('/brochures/', { method: 'POST', body: JSON.stringify(data) });
  deleteBrochure = brochureId => this.fetch(`/brochures/${brochureId}/`, { method: 'DELETE' });
  getBrochures = building => this.fetch(`/brochures/?building=${building}`, { method: 'GET' });
	getBrochure = brochureId => this.fetch(`/brochures/${brochureId}/`, { method: 'GET' });
	updateBrochure = ({ brochureId, data }) => this.fetch(`/brochures/${brochureId}/`, { method: 'PATCH', body: JSON.stringify(data) });
	cloneBrochure = (brochureId, name) => this.fetch(`/brochures/${brochureId}/clone/${name}/`, { method: 'GET' });
	cloneBrochureWithSpaces = (id, name, spaces, type, space) => this.fetch(`/brochures/${id}/clone/`, { method: 'POST', body: JSON.stringify({
	  name,
	  spaces,
	  type,
	  space
	})});

	// COLLECTION
	createCollection = brochures => this.fetch('/collection/', { method: 'POST', body: JSON.stringify({ brochures }) });
	readCollection = id => this.fetch(`/collection/${id}/`, { method: 'GET' });
	readCollectionBrochures = id => this.fetch(`/collection/${id}/brochures/`, { method: 'GET' });
	readUserCollections = () => this.fetch('/collections/', { method: 'GET' }).then(response => response.result.collections);
	updateCollection = ({ id, brochures, data }) => this.fetch(`/collection/${id}/`, { method: 'PATCH', body: JSON.stringify({ brochures, data }) });
	saveCollection = ({ id, name, recipients, message, signature, cc }) => this.fetch(`/collection/${id}/save/`, { method: 'PATCH', body: JSON.stringify({ name, recipients, message, signature, cc }) });
	updateAndSendCollection = ({ id, name, recipients, message, signature, cc }) => this.fetch(`/collection/${id}/send/`, { method: 'PATCH', body: JSON.stringify({ name, recipients, message, signature, cc }) });
	deleteCollection = id => this.fetch(`/collection/${id}/`, { method: 'DELETE' });

	// CONTENT
	updateContent = ({ brochureId, pageId, pageContent }) => this.fetch(`/content/${brochureId}/${pageId}/`, { method: 'PATCH', body: JSON.stringify({ pageContent }) });
	movePage = ({ brochureId, pageFrom, pageTo }) => this.fetch(`/content/${brochureId}/sort/`, { method: 'POST', body: JSON.stringify({ pageFrom, pageTo }) });
	deletePage = ({ brochureId, pageId }) => this.fetch(`/content/${brochureId}/${pageId}/`, { method: 'DELETE' });
	deletePages = ({ brochureId, pageIds }) => this.fetch(`/content/${brochureId}/pages/`, { method: 'DELETE', body: JSON.stringify({ pageIds }) });

	// PDF
	getPdf = ({ brochureId, quality }) => (
	  this.fetch(`/pdf/${brochureId}/?quality=${quality}`, { method: 'GET' })
	);

	generateUuid = () => {
	  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
	    // eslint-disable-next-line
			var r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
	    return v.toString(16);
	  });
	}

}
